import React, { useState, useMemo } from "react";
import { IoArrowBack } from "react-icons/io5";
import { Upload, Download, FileSpreadsheet, X } from "lucide-react";
import * as XLSX from "xlsx";
// exceljs est chargé à la demande dans handleExport (voir aussi
// Portefeuille/ExportToExcel.js) : importé statiquement, il pesait dans le
// bundle initial de toute l'application pour un écran réservé aux admins.
import { saveAs } from "file-saver";

// Colonnes attendues dans le fichier importé (export "Liste des clients" —
// voir Copie de Liste des clients-07-08-2026.xlsx) — noms exacts des
// en-têtes de colonne tels qu'exportés.
const COL_RAISON_SOCIALE = "Raison Sociale";
const COL_NOM = "Nom";
const COL_ADRESSE = "Adresse";
const COL_CODE_POSTAL = "Code Postal";
const COL_VILLE = "Ville";
const COL_TELEPHONE = "Téléphone Fixe";
const COL_MAIL = "Mail";
const COL_FONCTION = "Fonction du contact";

// Format cible — en-têtes/ordre exacts attendus par MEG pour l'import
// "Liste des contacts" (voir export_contacts (1).xlsx). Toutes les colonnes
// non listées ci-dessous n'ont pas d'équivalent dans le fichier source et
// restent vides (RIB, TVA, etc. ne sont pas dans l'export "Liste des clients").
const OUTPUT_HEADERS = [
  "Code",
  "Type",
  "Civilité",
  "Nom",
  "Prenom",
  "Raison sociale",
  "Forme juridique",
  "Famille",
  "Téléphone 1",
  "Portable",
  "Fax",
  "Email",
  "Site web",
  "Informations TVA N° TVA Intra",
  "Commentaire",
  "Adresse principale Adresse 1",
  "Adresse principale Adresse 2",
  "Adresse principale Adresse 3",
  "Adresse principale Code postal",
  "Adresse principale Cedex",
  "Adresse principale Ville",
  "Adresse principale Pays",
  "Banque 1 Nom",
  "Banque 1 Domiciliation",
  "Banque 1 Banque",
  "Banque 1 Guichet",
  "Banque 1 Compte",
  "Banque 1 Clé",
  "Banque 1 IBAN",
  "Banque 1 BIC",
  "Banque 1 RUM",
  "Banque 1 Date de signature",
  "Banque 1 Client déjà prélevé via SEPA",
  "Compte comptable",
  "Informations TVA Localisation",
  "Informations TVA Type d'activité",
  "Informations TVA Type de TVA",
];

// Colonnes du fichier source qui ont un équivalent direct dans le format
// cible (le reste — RIB, TVA, forme juridique... — n'existe pas dans
// l'export "Liste des clients" et reste donc vide).
const MAPPED_HEADERS = new Set([
  "Code",
  "Type",
  "Nom",
  "Raison sociale",
  "Téléphone 1",
  "Email",
  "Commentaire",
  "Adresse principale Adresse 1",
  "Adresse principale Code postal",
  "Adresse principale Ville",
  "Adresse principale Pays",
]);

const pad2 = (n) => String(n).padStart(2, "0");
const buildTimestampedFileName = () => {
  const now = new Date();
  const date = `${pad2(now.getDate())}-${pad2(now.getMonth() + 1)}-${now.getFullYear()}`;
  const time = `${pad2(now.getHours())}h${pad2(now.getMinutes())}`;
  return `import_to_meg_${date}_${time}.xlsx`;
};

// Section ADMIN — réservée aux administrateurs (même gating que Identifiants
// PA, voir Home.js). Convertit un export "Liste des clients" (format MEG,
// 8 colonnes séparées) vers le format d'import "Liste des contacts" attendu
// par MEG (36 colonnes, voir export_contacts (1).xlsx, code CLTxxx auto-généré).
// Demande du 07/09/2026.
function ImportMEG({ onCancel }) {
  const [fileName, setFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [startNumber, setStartNumber] = useState(2);
  const [error, setError] = useState(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (rows.length === 0) {
        setError("Le fichier ne contient aucune ligne de données.");
        setParsedRows([]);
        return;
      }
      setParsedRows(rows);
    } catch (err) {
      console.error("Erreur lecture fichier :", err);
      setError("Impossible de lire ce fichier — vérifiez qu'il s'agit bien d'un export Excel (.xls/.xlsx).");
      setParsedRows([]);
    }
  };

  const clearFile = () => {
    setFileName("");
    setParsedRows([]);
    setError(null);
  };

  // Recalculé à chaque changement de fichier ou de numéro de départ — la
  // liste affichée en aperçu EST exactement ce qui sera exporté.
  const transformedRows = useMemo(() => {
    return parsedRows.map((row, index) => {
      const cleanValue = (v) => (v === undefined || v === null ? "" : String(v).trim());
      const line = {};
      OUTPUT_HEADERS.forEach((h) => {
        line[h] = "";
      });
      line["Code"] = `CLT${String(startNumber + index).padStart(3, "0")}`;
      line["Type"] = "CLIENT";
      line["Nom"] = cleanValue(row[COL_NOM]);
      line["Raison sociale"] = cleanValue(row[COL_RAISON_SOCIALE]);
      line["Téléphone 1"] = cleanValue(row[COL_TELEPHONE]);
      line["Email"] = cleanValue(row[COL_MAIL]);
      line["Commentaire"] = cleanValue(row[COL_FONCTION]);
      line["Adresse principale Adresse 1"] = cleanValue(row[COL_ADRESSE]);
      line["Adresse principale Code postal"] = cleanValue(row[COL_CODE_POSTAL]);
      line["Adresse principale Ville"] = cleanValue(row[COL_VILLE]);
      line["Adresse principale Pays"] = "FRANCE";
      return line;
    });
  }, [parsedRows, startNumber]);

  const handleExport = async () => {
    if (transformedRows.length === 0) return;
    // Voir Portefeuille/ExportToExcel.js : exceljs est du CommonJS pur, on
    // accepte l'export racine comme la forme `default` selon l'interop.
    const mod = await import("exceljs");
    const ExcelJS = mod.default || mod;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Liste des contacts");

    worksheet.addRow(OUTPUT_HEADERS);
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF003366" },
      };
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    transformedRows.forEach((row) => {
      worksheet.addRow(OUTPUT_HEADERS.map((h) => row[h]));
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, buildTimestampedFileName());
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-1">
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-[#840040] transition-colors flex-shrink-0"
        >
          <IoArrowBack size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">MEG — Conversion Liste des clients</h1>
      </div>
      <p className="text-sm text-gray-400 mb-6 ml-8">
        Importe un export "Liste des clients" et le reformate au format d'import "Liste des
        contacts" attendu par MEG. Seules les colonnes disponibles dans le fichier source sont
        renseignées, le reste (RIB, TVA, forme juridique...) reste vide. Première version — à
        ajuster selon besoin.
      </p>

      <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-5">
        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#5C1A30] text-white text-sm font-semibold hover:bg-[#4a1526] transition-colors cursor-pointer">
            <Upload className="w-4 h-4" />
            Choisir un fichier Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>

          {fileName && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700">
              <FileSpreadsheet className="w-4 h-4 text-[#5C1A30]" />
              {fileName}
              <button onClick={clearFile} className="text-gray-400 hover:text-red-500 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">
              Code de départ (CLT...)
            </label>
            <input
              type="number"
              min="1"
              value={startNumber}
              onChange={(e) => setStartNumber(Math.max(1, Number(e.target.value) || 1))}
              className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#840040]/20 focus:border-[#840040]"
            />
          </div>
        </div>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      </div>

      {transformedRows.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">{transformedRows.length}</span>{" "}
              ligne(s) prête(s) — aperçu ci-dessous (colonnes renseignées uniquement), identique à
              ce qui sera exporté.
            </p>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#840040] text-white text-sm font-semibold hover:bg-[#6c0034] transition-colors"
            >
              <Download className="w-4 h-4" />
              Exporter au format MEG
            </button>
          </div>

          <div className="bg-white border border-gray-100 rounded-2xl overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#fff6ee] text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                  {OUTPUT_HEADERS.filter((h) => MAPPED_HEADERS.has(h)).map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transformedRows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/60 transition-colors">
                    {OUTPUT_HEADERS.filter((h) => MAPPED_HEADERS.has(h)).map((h) => (
                      <td key={h} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {row[h] || <span className="text-gray-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Le fichier exporté contient aussi toutes les colonnes vides du format MEG (RIB, TVA,
            forme juridique...), non affichées ici pour la lisibilité de l'aperçu.
          </p>
        </>
      )}
    </div>
  );
}

export default ImportMEG;
